import { css } from '@emotion/react';
import { NO_BREAK_SPACE } from '~/_shared/constants/text.constants';
import type { Theme } from '~/_shared/themes/theme.model';
import type { ExportReportTableValue } from '~/exportReport/table/ReportTable.model';
import type { ReportTemplateDataRow } from '../template/reportTemplate';

const rowStyle = ({ style, isInteractive }: { style?: ReportTemplateDataRow['style']; isInteractive?: boolean }) => (theme: Theme) => css({
  borderBottom: `1px solid ${theme.reportColors.borderPrimary}`,
  breakInside: 'avoid',
  color: style === 'highlight' ? theme.textColors.info : theme.reportColors.textPrimary,
  cursor: isInteractive ? 'pointer' : undefined,
  fontWeight: style === 'bold' ? 700 : style === 'highlight' ? undefined : 400,
  fontStyle: style === 'accent' ? 'italic' : undefined,
  'tbody &:nth-of-type(2n)': {
    background: theme.reportColors.tableRowBackgroundPrimary,
  },
  'tbody &:nth-of-type(2n + 1)': {
    background: theme.reportColors.tableRowBackgroundSecondary,
  },
});

const tableDataCellStyle = ({ isError, color }: { isError?: boolean; color?: string } = {}) => (theme: Theme) => css({
  textAlign: 'right',
  border: `0 solid ${theme.reportColors.borderPrimary}`,
  padding: 5, // vertical only; horizontal added per side below
  borderTopWidth: 0,
  borderBottomWidth: 0,
  color: isError ? 'red' : color,
  fontWeight: isError ? 700 : undefined,
  '&:first-of-type': {
    borderLeftWidth: 1,
    paddingLeft: 8,
    textAlign: 'left',
  },
  '&:last-of-type': {
    borderRightWidth: 1,
    paddingRight: 8,
  },
  'tr:first-of-type &': {
    borderTopWidth: 1,
  },
  'tr:last-of-type &': {
    borderBottomWidth: 1,
  },
  'tr:first-of-type &:first-of-type': {
    borderTopLeftRadius: 8,
  },
  'tr:last-of-type &:first-of-type': {
    borderBottomLeftRadius: 8,
  },
  'tr:first-of-type &:last-of-type': {
    borderTopRightRadius: 8,
  },
  'tr:last-of-type &:last-of-type': {
    borderBottomRightRadius: 8,
  },
});

const MINOR_WORDS = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'so', 'yet', 'at', 'by', 'in', 'of', 'on', 'to', 'up', 'as', 'is', 'it', 'with']);

const toTitleCase = (text: string): string =>
  text.replace(/\b\w+(?:'\w+)*\b/g, (word, offset: number) => {
    const lower = word.toLowerCase();
    return offset === 0 || !MINOR_WORDS.has(lower) ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  });

type ExportReportTableDataRowProps = Readonly<{
  style?: ReportTemplateDataRow['style'];
  isInteractive?: boolean;
  isLabelError?: boolean;
  label: string;
  onClick?: () => void;
  values: ReadonlyArray<ExportReportTableValue>;
}>;

export const ExportReportTableDataRowComponent = (props: ExportReportTableDataRowProps) => {
  return (
    <tr css={rowStyle({ style: props.style, isInteractive: props.isInteractive })} onClick={props.onClick}>
      <td css={tableDataCellStyle({ isError: props.isLabelError })}>
        {props.label ? toTitleCase(props.label) : NO_BREAK_SPACE}
      </td>
      {props.values.map((item, index) => (
        <td key={index} css={tableDataCellStyle({ isError: item.isError, color: item.color })}>
          <span>
            {item.value}
          </span>
        </td>
      ))}
    </tr>
  );
};
